import { ArrowPathIcon, LinkIcon, PlusIcon } from '@heroicons/react/20/solid';
import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { Alert } from '@mui/material';
import { Loader2, XCircle } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { MdAutoMode } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

import { CompanionLabel } from '@/components/common/CompanionLabel';
import { DataTable, TableColumn } from '@/components/common/DataTable';
import {
  humanizeStatus,
  StatusPill,
} from '@/components/common/DataTable/StatusPill';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePagination } from '@/hooks/usePagination';
import { useListIntakeChasesQuery } from '@/services/api/intakeChaseService';
import {
  useGetIntakeListMetadataQuery,
  useGetIntakesQuery,
  useSetNoMatterNeededMutation,
} from '@/services/api/intakeService';
import type { IntakeChase } from '@/services/types/intake-chase-types';
import { IntakeChaseStatus } from '@/services/types/intake-chase-types';
import { ALL_INTAKE_STATUSES, IntakeListItem, IntakeStatus } from '@/services/types/intake-types';
import { IntakeFilterParams } from '@/services/types/pagination-types';
import { getResults, getTotalCount, toQueryParams } from '@/utils/pagination';
import { getCallabilityInfo, TIMEZONE_FILTER_OPTIONS } from '@/utils/timezone';

import IntakeLayout from '../pages/IntakeLayout';
import {
  AutoChaseIndicator,
  AutoChaseTableCell,
} from './components/AutoChaseTableCell';
import { BulkMarkNoticeSentAction } from './components/BulkMarkNoticeSentAction';
import { IntakeMetricsBoxes } from './components/IntakeMetricsBoxes';
import { LinkIntakesModal } from './components/LinkIntakesModal';
import { StatusFilterButtons } from './components/StatusFilterButtons';
import { TimezonePill } from './components/TimezonePill';

/**
 * Threshold for stale intake activity (days)
 */
export const STALE_ACTIVITY_DAYS = 7;

/**
 * Lead source filter options (matches LeadSourceSelect component)
 */
const LEAD_SOURCE_OPTIONS = [
  { label: 'Inbound Phone Call', value: 'Inbound Phone Call' },
  { label: 'Email - Facebook Lead', value: 'Email - Facebook Lead' },
  { label: 'Email - Webform', value: 'Email - Webform' },
  { label: 'Email - LSA', value: 'Email - LSA' },
  { label: 'Email - Attorney Provided', value: 'Email - Attorney Provided' },
  { label: 'Text', value: 'Text' },
];

/**
 * Callability group labels — keyed by backend's callability_group values.
 * Using the backend value directly avoids FE/BE computation drift.
 */
const CALLABILITY_GROUP_LABELS: Record<string, string> = {
  callable: 'Callable Now',
  not_yet_eastern: 'Not Yet Callable (Eastern)',
  not_yet_central: 'Not Yet Callable (Central)',
  not_yet_mountain: 'Not Yet Callable (Mountain)',
  not_yet_pacific: 'Not Yet Callable (Pacific)',
  not_yet_alaska_hawaii: 'Not Yet Callable (AK/HI)',
  no_state: 'No State Info \u2014 Needs Attention',
  after_hours: 'After Hours',
};

/**
 * Column definitions factory for intakes table
 * Defined outside component to avoid re-creation on every render
 */
const createIntakeColumns = (
  firmOptions: Array<{ label: string; value: string }>,
  creatorOptions: Array<{ label: string; value: string }>,
  assignedToOptions: Array<{ label: string; value: string }>,
  chaseLookup: Map<string, IntakeChase>,
): TableColumn<IntakeListItem>[] => [
  {
    id: 'client_name',
    header: 'Client Name',
    accessorKey: 'client_name',
    width: '200px',
    sticky: true,
    cell: (row) => (
      <div className="flex items-center gap-2">
        <span
          className="text-black dark:text-white truncate"
          title={row.client_name}
        >
          {row.client_name}
        </span>
        <CompanionLabel
          memberCount={row.companion_count}
          isPrimary={row.is_companion_primary}
          size="sm"
        />
      </div>
    ),
  },
  {
    id: 'timezone',
    header: 'Timezone',
    accessorKey: 'incident_state',
    width: '80px',
    cell: (row) => <TimezonePill stateAbbrev={row.incident_state} />,
    filter: {
      type: 'multiselect',
      options: TIMEZONE_FILTER_OPTIONS,
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    width: '160px',
    cell: (row) => (
      <div className="flex items-center gap-1.5">
        <StatusPill status={row.status as string} size="sm" />
        <AutoChaseIndicator chase={chaseLookup.get(row.id) ?? null} />
      </div>
    ),
    filter: {
      type: 'multiselect',
      options: ALL_INTAKE_STATUSES.map((s) => ({
        label: humanizeStatus(s),
        value: s,
      })),
    },
  },
  {
    id: 'has_matter',
    header: 'Has Matter',
    accessorKey: 'has_matter',
    align: 'center',
    width: '100px',
    sortable: true,
    cell: (row, value) => {
      const hasMatter = value as boolean | null | undefined;
      if (hasMatter === null || hasMatter === undefined) {
        return <span className="text-gray-400">—</span>;
      }
      return (
        <span className={hasMatter ? 'text-green-600' : 'text-gray-500'}>
          {hasMatter ? 'Yes' : 'No'}
        </span>
      );
    },
    filter: {
      type: 'select',
      options: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
    },
  },
  {
    id: 'notes_count',
    header: 'Notes',
    accessorKey: 'notes_count',
    align: 'center',
    width: '100px',
    cell: (row, value) => {
      const count = value as number;
      const latestNote = row.latest_note as string | undefined;

      if (count === 0) {
        return <span className="text-gray-400">0</span>;
      }

      const isHighCount = count >= 5;
      const textColor = isHighCount
        ? 'text-red-600'
        : 'text-black dark:text-white';

      if (!latestNote || latestNote.trim() === '') {
        return <span className={textColor}>{count}</span>;
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`cursor-help ${textColor}`}>{count}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            <p className="text-xs">{latestNote}</p>
          </TooltipContent>
        </Tooltip>
      );
    },
  },
  {
    id: 'firm_name',
    header: 'Firm',
    accessorKey: 'firm_name',
    width: '200px',
    cell: (row, value) => (
      <span
        className="text-gray-700 dark:text-zinc-300 block truncate"
        title={value as string}
      >
        {value as string}
      </span>
    ),
    filter: {
      type: 'multiselect',
      options: firmOptions,
      searchable: true,
    },
  },
  {
    id: 'referral_source_description',
    header: 'Referral Source',
    accessorKey: 'referral_source_description',
    cell: (row, value) => {
      const referralText = value as string | undefined;
      if (!referralText || referralText.trim() === '') {
        return <span className="text-gray-400">—</span>;
      }
      return (
        <span
          className="text-black dark:text-white truncate"
          title={referralText}
        >
          {referralText}
        </span>
      );
    },
  },
  {
    id: 'rejection_reason',
    header: 'Rejection Reason',
    accessorKey: 'rejection_reason',
    cell: (row, value) => {
      const reason = value as string | undefined;
      if (!reason || reason.trim() === '') {
        return <span className="text-gray-400">—</span>;
      }
      return (
        <span
          className="text-red-600 dark:text-red-400 truncate"
          title={reason}
        >
          {reason}
        </span>
      );
    },
  },
  {
    id: 'is_scheduled',
    header: 'Scheduled',
    accessorKey: 'is_scheduled',
    width: '130px',
    cell: (row) => {
      if (!row.is_scheduled || !row.scheduled_at) {
        return <span className="text-gray-400">&mdash;</span>;
      }
      const date = new Date(row.scheduled_at);
      const dateStr = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      });
      const assigneeName = row.scheduled_assignee_name || '';
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex flex-col cursor-help">
              <span className="text-xs text-green-700">
                {dateStr} {timeStr}
              </span>
              {assigneeName && (
                <span className="text-[11px] text-gray-400 truncate leading-tight">
                  {assigneeName}
                </span>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-xs space-y-0.5">
              <p>
                <span className="font-medium">When:</span>{' '}
                {date.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </p>
              {row.scheduled_assignee_name && (
                <p>
                  <span className="font-medium">Primary:</span>{' '}
                  {row.scheduled_assignee_name}
                </p>
              )}
              {row.scheduled_backup_assignee_name && (
                <p>
                  <span className="font-medium">Backup:</span>{' '}
                  {row.scheduled_backup_assignee_name}
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      );
    },
    filter: {
      type: 'select',
      options: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
    },
  },
  {
    id: 'rejection_email_sent',
    header: 'Rejection Email Sent',
    accessorKey: 'rejection_email_sent',
    align: 'center',
    width: '140px',
    cell: (row, value) => {
      const sent = value as boolean | undefined;
      if (sent) {
        return (
          <span className="text-green-600 flex items-center justify-center">
            <CheckIcon className="h-4 w-4" />
          </span>
        );
      }
      return <span className="text-gray-500 text-xs font-medium">No</span>;
    },
    filter: {
      type: 'select',
      options: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
    },
  },
  {
    id: 'lead_source',
    header: 'Lead Source',
    accessorKey: 'lead_source',
    cell: (row, value) => {
      const leadSource = value as string | undefined;
      if (!leadSource || leadSource.trim() === '') {
        return <span className="text-gray-400">—</span>;
      }
      return (
        <span
          className="text-black dark:text-white truncate"
          title={leadSource}
        >
          {leadSource}
        </span>
      );
    },
    filter: {
      type: 'multiselect',
      options: LEAD_SOURCE_OPTIONS,
    },
  },
  {
    id: 'creator_name',
    header: 'Creator',
    accessorKey: 'creator_name',
    cell: (row, value) => {
      if (!value) return <span className="text-gray-400">—</span>;
      return (
        <span className="text-black dark:text-white">{value as string}</span>
      );
    },
    filter: {
      type: 'multiselect',
      options: creatorOptions,
      searchable: true,
    },
  },
  {
    id: 'assigned_to_name',
    header: 'Assigned To',
    accessorKey: 'assigned_to_name',
    cell: (row, value) => {
      if (!value) return <span className="text-gray-400">—</span>;
      return (
        <span className="text-black dark:text-white">{value as string}</span>
      );
    },
    filter: {
      type: 'multiselect',
      options: assignedToOptions,
      searchable: true,
    },
  },
  {
    id: 'manual_qualification',
    header: 'Qualification',
    accessorKey: 'manual_qualification',
    align: 'center',
    width: '120px',
    cell: (row, value) => {
      if (!value) {
        return <span className="text-gray-400">—</span>;
      }
      return <StatusPill status={value as string} size="sm" />;
    },
    filter: {
      type: 'multiselect',
      options: [
        { label: 'Qualified', value: 'qualified' },
        { label: 'Unqualified', value: 'unqualified' },
        { label: 'Not Set', value: 'null' },
      ],
    },
  },
  {
    id: 'created_at',
    header: 'Date Created',
    accessorKey: 'created_at',
    sortable: true,
    cell: (row, value) => {
      if (!value) return <span className="text-gray-400">—</span>;
      const date = new Date(value as string);
      return (
        <span className="text-black dark:text-white">
          {date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          })}
        </span>
      );
    },
  },
  {
    id: 'latest_note_at',
    header: 'Last Note Taken',
    accessorKey: 'latest_note_at',
    sortable: true,
    cell: (row, value) => {
      if (!value) return <span className="text-gray-400">—</span>;

      const date = new Date(value as string);
      const now = new Date();

      // Compare calendar dates in the user's local timezone
      const dateAtMidnight = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );
      const nowAtMidnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
      );
      const diffInMs = nowAtMidnight.getTime() - dateAtMidnight.getTime();
      const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));

      // Highlight if >= 7 days
      const isStale = diffInDays >= STALE_ACTIVITY_DAYS;
      const textColor = isStale ? 'text-red-600' : 'text-black dark:text-white';

      const displayText =
        diffInDays === 0
          ? 'Today'
          : `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;

      const formattedDate = date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`cursor-help ${textColor}`}>{displayText}</span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{formattedDate}</p>
          </TooltipContent>
        </Tooltip>
      );
    },
  },
];

/**
 * Auto Chase column definition factory.
 * Defined outside the component to avoid react/no-unstable-nested-components lint error.
 */
const createAutoChaseColumn = (
  chaseLookup: Map<string, IntakeChase>,
): TableColumn<IntakeListItem> => ({
  id: 'auto_chase',
  header: 'Auto Chase',
  accessorKey: 'id',
  width: '150px',
  cell: (row) => (
    <AutoChaseTableCell chase={chaseLookup.get(row.id) ?? null} />
  ),
});

/**
 * "No Matter Needed" cell component for the unconverted view.
 * Calls the setNoMatterNeeded mutation inline and removes the row from view.
 */
const NoMatterNeededCell: React.FC<{ intakeId: string }> = ({ intakeId }) => {
  const [setNoMatterNeeded, { isLoading }] = useSetNoMatterNeededMutation();

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await setNoMatterNeeded({ intakeId, value: true });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-gray-600 hover:text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50"
    >
      {isLoading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <XCircle className="h-3.5 w-3.5" />
      )}
      No Matter Needed
    </button>
  );
};

/**
 * "No Matter Needed" column for the unconverted view.
 */
const createNoMatterNeededColumn = (): TableColumn<IntakeListItem> => ({
  id: 'no_matter_needed',
  header: '',
  accessorKey: 'id',
  width: '160px',
  cell: (row) => <NoMatterNeededCell intakeId={row.id} />,
});

/**
 * Intakes page using new DataTable component
 */
export const Intakes = () => {
  const navigate = useNavigate();

  // Setup pagination (includes debounced search)
  const { state, actions, searchInput, setSearchInput } = usePagination({
    defaultLimit: 50,
    defaultSort: '-created_at',
  });

  const [autoChaseOnly, setAutoChaseOnly] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [isLinkIntakesOpen, setIsLinkIntakesOpen] = useState(false);

  // Get current active status filter
  const activeStatusFilter = useMemo(() => {
    const { status } = state.filters;
    if (!status) return null;
    return Array.isArray(status) ? status[0] : status;
  }, [state.filters]);

  // Activate row grouping when viewing chase-related filters.
  // Groups: "Callable Now" -> "Opens at 10:00 AM (CST)" -> "Opens at 12:00 PM (PST)" -> "After Hours"
  const isChaseView =
    activeStatusFilter === IntakeStatus.CHASE ||
    state.filters.chase_today === 'true';

  // Fetch data — inject has_active_chase when autoChaseOnly toggle is on.
  // When autoChaseOnly is active, remove the status filter so intakes with
  // active chases show regardless of their current intake status.
  const queryParams = useMemo(() => {
    const params = toQueryParams<IntakeFilterParams>(state);
    if (autoChaseOnly && isChaseView) {
      params.has_active_chase = 'true';
      // Remove all status-related filters so active chases show regardless of intake status
      delete params.status;
      delete (params as Record<string, unknown>).chase_today;
    }
    return params;
  }, [state, autoChaseOnly, isChaseView]);


  const { data, isLoading, error } = useGetIntakesQuery(queryParams);
  const { data: metadata } = useGetIntakeListMetadataQuery();

  const allIntakes = getResults(data);
  const rawTotalCount = getTotalCount(data);

  // Prepare filter options from metadata (memoized to avoid recreation)
  const firmOptions = useMemo(
    () =>
      (metadata?.firms || []).map((firm) => ({
        label: firm.name,
        value: firm.id,
      })),
    [metadata?.firms],
  );

  const creatorOptions = useMemo(
    () =>
      (metadata?.creators || [])
        .filter((creator) => creator.name && creator.name.trim() !== '')
        .map((creator) => ({
          label: creator.name,
          value: creator.id,
        })),
    [metadata?.creators],
  );

  const assignedToOptions = useMemo(
    () =>
      (metadata?.assigned_users || [])
        .filter((user) => user.name && user.name.trim() !== '')
        .map((user) => ({
          label: user.name,
          value: user.id,
        })),
    [metadata?.assigned_users],
  );

  // Calculate total count from status counts (for metrics box)
  const totalIntakesCount = useMemo(() => {
    if (!metadata?.status_counts) return rawTotalCount;
    return metadata.status_counts.reduce((sum, item) => sum + item.count, 0);
  }, [metadata?.status_counts, rawTotalCount]);

  // Fetch auto chases to display indicators and the chase column.
  // When autoChaseOnly is on, widen to all statuses so completed/stopped chases
  // with failures also appear in the table.
  const chaseQueryParams = useMemo(() => {
    if (autoChaseOnly) {
      return {}; // No status filter — fetch all chases
    }
    return { status: 'active,needs_attention,paused' };
  }, [autoChaseOnly]);

  const { data: activeChases } = useListIntakeChasesQuery(chaseQueryParams);

  // Build a lookup map from intake ID to chase
  const chaseLookup = useMemo(() => {
    const map = new Map<string, IntakeChase>();
    if (activeChases) {
      activeChases.forEach((chase) => {
        map.set(chase.intake, chase);
      });
    }
    return map;
  }, [activeChases]);

  // Count chases that need attention (PC responded)
  const needsAttentionCount = useMemo(
    () =>
      activeChases?.filter(
        (c) => c.status === IntakeChaseStatus.NEEDS_ATTENTION,
      ).length ?? 0,
    [activeChases],
  );

  // Count of active auto chases (for the filter badge)
  const activeChaseCount = activeChases?.length ?? 0;

  // When in chase view or autoChaseOnly, sort needs_attention chases to the top
  const intakes = useMemo(() => {
    if (!isChaseView && !autoChaseOnly) return allIntakes;

    return [...allIntakes].sort((a, b) => {
      const chaseA = chaseLookup.get(a.id);
      const chaseB = chaseLookup.get(b.id);
      const aNeedsAttention = chaseA?.status === IntakeChaseStatus.NEEDS_ATTENTION ? 1 : 0;
      const bNeedsAttention = chaseB?.status === IntakeChaseStatus.NEEDS_ATTENTION ? 1 : 0;
      return bNeedsAttention - aNeedsAttention;
    });
  }, [allIntakes, isChaseView, autoChaseOnly, chaseLookup]);

  // Detect unconverted view
  const isUnconvertedView = state.filters.unconverted === 'true';

  // Column definitions (memoized, only recreated when filter options change)
  // TZ column is only shown in chase views (chase status or chase_today metric)
  const intakeColumns = useMemo(
    () => {
      const cols = createIntakeColumns(
        firmOptions,
        creatorOptions,
        assignedToOptions,
        chaseLookup,
      );

      if (isUnconvertedView) {
        // In unconverted view: remove timezone, add "No Matter Needed" as second column
        const filtered = cols.filter((c) => c.id !== 'timezone');
        filtered.splice(1, 0, createNoMatterNeededColumn());
        return filtered;
      }

      if (!isChaseView) return cols.filter((c) => c.id !== 'timezone');

      // Add Auto Chase column after the status column in chase views
      const statusIndex = cols.findIndex((c) => c.id === 'status');
      const autoChaseColumn: TableColumn<IntakeListItem> =
        createAutoChaseColumn(chaseLookup);
      const result = [...cols];
      result.splice(statusIndex + 1, 0, autoChaseColumn);
      return result;
    },
    [firmOptions, creatorOptions, assignedToOptions, isChaseView, isUnconvertedView, chaseLookup],
  );

  /**
   * Returns a class that visually mutes intake rows outside callable hours.
   * Only applied to chase-status leads so other statuses are unaffected.
   * On hover the opacity restores so the row remains fully interactive.
   */
  const getRowClassName = (row: IntakeListItem): string => {
    if (row.status !== IntakeStatus.CHASE) return '';

    // Highlight rows where auto chase needs attention (PC responded)
    const chase = chaseLookup.get(row.id);
    if (chase?.status === IntakeChaseStatus.NEEDS_ATTENTION) {
      return 'bg-amber-50 hover:bg-amber-100 transition-colors';
    }

    const info = getCallabilityInfo(row.incident_state);
    // No state set or unrecognized → default to fully visible (EST assumption)
    if (!info) return '';
    if (info.isCallable) return '';

    return 'opacity-60 hover:opacity-100 transition-opacity';
  };

  const chaseGrouping = useMemo(
    () =>
      isChaseView
        ? {
            getGroupKey: (row: IntakeListItem): string =>
              row.callability_group || 'no_state',
            getGroupLabel: (key: string): string =>
              CALLABILITY_GROUP_LABELS[key] || key,
          }
        : undefined,
    [isChaseView],
  );

  // Handlers
  const handleCreateIntake = () => {
    sessionStorage.setItem('intakesSearch', window.location.search);
    navigate('/intakes/create');
  };

  const handleReset = () => {
    setSearchInput(''); // Clear local search input
    setAutoChaseOnly(false); // Clear auto chase filter
    actions.reset();
  };

  const handleRowClick = (intake: IntakeListItem) => {
    sessionStorage.setItem('intakesSearch', window.location.search);
    navigate(`/intakes/${intake.id}`);
  };

  const handleRowContextMenu = (intake: IntakeListItem) => {
    return [
      {
        label: 'Open in new window',
        icon: <ArrowTopRightOnSquareIcon className="h-4 w-4" />,
        onClick: () => {
          const url = `/intakes/${intake.id}`;
          window.open(url, '_blank', 'noopener,noreferrer');
        },
      },
    ];
  };

  const [copySuccess, setCopySuccess] = useState(false);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // Silently fail if clipboard access is denied
      setCopySuccess(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value); // Update local state immediately (no lag)
  };


  // Handle column filter changes
  const handleFilterChange = (
    columnId: string,
    value: string | string[] | null,
  ) => {
    // Map column IDs to backend filter params
    const filterKeyMap: Record<string, string> = {
      status: 'status',
      firm_name: 'firm',
      creator_name: 'creator',
      assigned_to_name: 'assigned_to',
      lead_source: 'lead_source',
      has_matter: 'has_matter',
      rejection_email_sent: 'rejection_email_sent',
      is_scheduled: 'is_scheduled',
      timezone: 'timezone',
      manual_qualification: 'manual_qualification',
    };

    const filterKey = filterKeyMap[columnId] || columnId;

    // Update filters while preserving other filters
    const newFilters = { ...state.filters };

    if (value === null || (Array.isArray(value) && value.length === 0)) {
      // Remove filter if value is null or empty array
      delete newFilters[filterKey];
    } else {
      // Set filter value
      newFilters[filterKey] = value;
    }

    actions.setFilters(newFilters);
  };

  // Map backend filter values to column filter values for display
  const filterValues = useMemo(() => {
    const columnFilterMap: Record<string, string> = {
      firm: 'firm_name',
      creator: 'creator_name',
      assigned_to: 'assigned_to_name',
      status: 'status',
      lead_source: 'lead_source',
      has_matter: 'has_matter',
      rejection_email_sent: 'rejection_email_sent',
      is_scheduled: 'is_scheduled',
      timezone: 'timezone',
      manual_qualification: 'manual_qualification',
    };

    const result: Record<string, string | string[] | null> = {};

    Object.entries(state.filters).forEach(([key, value]) => {
      const columnId = columnFilterMap[key] || key;
      if (typeof value === 'boolean' || typeof value === 'number') {
        result[columnId] = String(value);
      } else {
        result[columnId] = value;
      }
    });

    return result;
  }, [state.filters]);

  // Get selected intake objects for bulk actions
  const selectedIntakes = useMemo(() => {
    return intakes.filter((i) => selectedRows.has(i.id));
  }, [intakes, selectedRows]);

  const handleBulkComplete = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  const renderBulkMarkNoticeSent = useMemo(
    () => (
      <BulkMarkNoticeSentAction
        selectedIntakes={selectedIntakes}
        onComplete={handleBulkComplete}
      />
    ),
    [selectedIntakes, handleBulkComplete],
  );

  return (
    <IntakeLayout>
      {/* Error state */}
      {error && (
        <div className="p-6">
          <Alert severity="error">
            Failed to load intakes. Please try refreshing the page.
          </Alert>
        </div>
      )}

      {/* Main content with padding */}
      {!error && (
        <div
          className="flex flex-col gap-2 px-10 py-2 bg-[#FAFAFA] dark:bg-zinc-950 overflow-hidden"
          style={{ height: 'calc(100vh - 64px)' }}
        >
          {/* Page title and metrics boxes row */}
          <div className="flex items-center justify-between flex-shrink-0">
            <h1 className="font-serif text-[32px] font-medium leading-[40px] text-black dark:text-white">
              Intakes
            </h1>

            {/* Metrics boxes aligned with title */}
            <div className="flex items-center gap-3">
              <IntakeMetricsBoxes
                paginationState={state}
                paginationActions={actions}
                totalCount={totalIntakesCount}
                statusCounts={metadata?.status_counts}
              />
            </div>
          </div>

          {/* Status filter pills and share button */}
          <div className="flex-shrink-0 flex items-center justify-between gap-4">
            <StatusFilterButtons
              paginationState={state}
              paginationActions={actions}
              statusCounts={metadata?.status_counts}
              needsAttentionCount={needsAttentionCount}
            />

            {/* Share button */}
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 rounded-md hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
            >
              {copySuccess ? (
                <>
                  <CheckIcon className="h-4 w-4 text-green-600" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <LinkIcon className="h-4 w-4" />
                  <span>Share</span>
                </>
              )}
            </button>
          </div>

          {/* White card container with table - scrollable */}
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-gray-300 dark:border-zinc-800 overflow-hidden flex-1 flex flex-col min-h-0">
            {/* DataTable */}
            <DataTable
              data={intakes}
              columns={intakeColumns}
              getRowKey={(row) => row.id}
              getRowClassName={getRowClassName}
              // Search
              searchEnabled
              searchPlaceholder="Search by name or phone..."
              searchValue={searchInput}
              onSearchChange={handleSearchChange}
              // Selection & bulk actions
              selectionEnabled
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
              bulkActions={[
                {
                  label: 'Mark Notice Sent',
                  customRender: () => renderBulkMarkNoticeSent,
                },
              ]}
              // Actions - with proper styling
              actions={[
                ...(isChaseView
                  ? [
                      {
                        label: `Auto Chase Only${activeChaseCount > 0 ? ` (${activeChaseCount})` : ''}`,
                        onClick: () => setAutoChaseOnly((prev) => !prev),
                        variant: 'plain' as const,
                        className: autoChaseOnly
                          ? '!bg-amber-500 hover:!bg-amber-600 !text-white !border !border-amber-500'
                          : '!border !border-gray-300 !text-gray-600 hover:!bg-gray-50 !bg-white',
                        icon: (
                          <MdAutoMode
                            className={`h-3.5 w-3.5 ${autoChaseOnly ? 'text-white' : 'text-amber-500'}`}
                          />
                        ),
                      },
                    ]
                  : []),
                {
                  label: 'Reset view',
                  icon: (
                    <ArrowPathIcon
                      className="h-5 w-5 !text-primary"
                      data-slot="icon"
                    />
                  ),
                  onClick: handleReset,
                  variant: 'plain',
                },
                {
                  label: 'New Intake',
                  icon: <PlusIcon className="h-5 w-5" />,
                  onClick: handleCreateIntake,
                  color: 'blue',
                  className: 'bg-primary hover:bg-primary-dark !items-center',
                },
              ]}
              menuActions={[
                {
                  label: 'Link Intakes',
                  icon: <LinkIcon className="h-4 w-4 mr-1" data-slot="icon" />,
                  onClick: () => setIsLinkIntakesOpen(true),
                },
              ]}
              // Pagination
              paginationEnabled
              currentPage={actions.getCurrentPage()}
              totalPages={actions.getTotalPages(rawTotalCount)}
              totalCount={rawTotalCount}
              pageSize={state.limit}
              onPageChange={actions.setPage}
              onPageSizeChange={actions.setLimit}
              // Sorting
              onSortChange={actions.setOrdering}
              sortField={state.ordering ? state.ordering.replace('-', '') : ''}
              sortDirection={state.ordering.startsWith('-') ? 'desc' : 'asc'}
              // Filtering
              onFilterChange={handleFilterChange}
              filterValues={filterValues}
              // States
              isLoading={isLoading}
              emptyMessage="No intakes found"
              // Interaction
              onRowClick={handleRowClick}
              onRowContextMenu={handleRowContextMenu}
              // Timezone-aware grouping (only active in chase views)
              grouping={chaseGrouping}
            />
          </div>
        </div>
      )}
      <LinkIntakesModal
        open={isLinkIntakesOpen}
        onOpenChange={setIsLinkIntakesOpen}
      />
    </IntakeLayout>
  );
};
